import React from 'react';
import { Link } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { LoadingSpinner } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  to?: string;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  fullWidth?: boolean;
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'text-sm px-4 py-2.5 min-h-[44px] gap-1.5',
  md: 'text-base px-6 py-3 min-h-[48px] gap-2',
  lg: 'text-lg px-8 py-4 min-h-[56px] gap-2.5',
};

const iconSizes: Record<ButtonSize, number> = {
  sm: 14,
  md: 18,
  lg: 22,
};

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-natuvia-ctaDark text-white rounded-lg hover:bg-natuvia-ctaDark/90 hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg',
  secondary:
    'bg-transparent border-2 border-natuvia-text text-natuvia-text rounded-lg hover:bg-natuvia-text hover:text-white hover:scale-[1.02] active:scale-[0.98]',
  ghost:
    'bg-transparent text-natuvia-text rounded-lg hover:bg-natuvia-text/10 active:bg-natuvia-text/20',
  danger:
    'bg-red-600 text-white rounded-lg hover:bg-red-700 hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg',
  link: 'text-natuvia-ctaDark hover:text-natuvia-ctaDark/80 hover:underline underline-offset-4 p-0 bg-transparent',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  to,
  onClick,
  className = '',
  type = 'button',
  disabled = false,
  loading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  fullWidth = false,
}) => {
  const isDisabled = disabled || loading;

  const baseStyles =
    'inline-flex items-center justify-center font-bold transition-all duration-300 ease-out cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-natuvia-highlight/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

  const classes = [
    baseStyles,
    variantStyles[variant],
    variant !== 'link' ? sizeStyles[size] : '',
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = iconSizes[size];

  const content = (
    <>
      {loading ? (
        <LoadingSpinner size={iconSize} />
      ) : LeftIcon ? (
        <LeftIcon size={iconSize} aria-hidden />
      ) : null}
      <span>{children}</span>
      {!loading && RightIcon && <RightIcon size={iconSize} aria-hidden />}
    </>
  );

  if (to && !isDisabled) {
    return (
      <Link to={to} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick} disabled={isDisabled}>
      {content}
    </button>
  );
};
